/**
 * Wallet error helpers.
 * ethers v6 wraps EIP-1193 errors (4001 becomes code 'ACTION_REJECTED', others become 'UNKNOWN_ERROR'
 * with the real code nested), so we dig out the raw provider code before deciding what to show.
 */
export function rpcErrorCode(err: any): number | string | undefined {
  const cands = [
    err?.code, err?.info?.error?.code, err?.error?.code, err?.data?.originalError?.code,
    err?.cause?.code, err?.info?.error?.data?.originalError?.code,
  ];
  for (const c of cands) if (typeof c === 'number') return c;
  if (err?.code === 'ACTION_REJECTED') return 4001;
  return err?.code;
}

export function rpcErrorMessage(err: any): string {
  return String(err?.info?.error?.message || err?.error?.message || err?.shortMessage || err?.message || err || '').slice(0, 160);
}

export class WalletStepError extends Error {
  step: 'accounts' | 'switch' | 'add';
  code: number | string | undefined;
  constructor(step: WalletStepError['step'], cause: any) {
    super(rpcErrorMessage(cause));
    this.step = step;
    this.code = rpcErrorCode(cause);
  }
}

/** Human-readable reason for a failed connect / network step. */
export function describeWalletError(err: any, networkName = 'Base Sepolia'): string {
  const code = rpcErrorCode(err);
  const step = err instanceof WalletStepError ? err.step : 'accounts';
  if (code === 4001) {
    return step === 'accounts' ? 'Connection rejected in your wallet.' : `Network switch to ${networkName} was rejected.`;
  }
  if (code === -32002) return 'Your wallet already has a pending request. Open the wallet extension to finish it.';
  if (code === 4100) return 'Wallet is locked or this site is not authorized. Unlock your wallet and try again.';
  if (code === 4900 || code === 4901) return 'Wallet is disconnected from the network. Check your wallet and retry.';
  if (code === 4902) return `${networkName} is not added to your wallet and adding it failed.`;
  if (step === 'switch' || step === 'add') return `Could not switch to ${networkName}: ${err.message || 'unknown error'}`;
  const msg = rpcErrorMessage(err);
  return msg ? `Connection failed: ${msg}` : 'Connection failed. Try again, anon!';
}

/** Preferred EIP-6963 wallets by rdns, in picker order. */
export const PREFERRED_RDNS = ['io.metamask', 'app.phantom'];
